import { BaseCommand } from './ICommand';
import { Node } from '../models/scene';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { useDiagramStore } from '../../stores/diagramStore';
import { validateShapeLimitOrPromptUpgrade } from '../../utils/shapeLimitUtils';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';

export interface FreehandPathProps {
  points: number[];
  color: string;
  width: number;
  opacity: number;
  mode: 'pen' | 'marker' | 'highlighter' | 'eraser';
}

export class CreateFreehandPathCommand extends BaseCommand {
  private readonly nodeTemplate: Node;

  constructor(props: FreehandPathProps) {
    super('Create Freehand Path', 'Draw freehand path');
    const id = `path-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.nodeTemplate = {
      id,
      type: 'freehand',
      x: 0,
      y: 0,
      attrs: {
        points: props.points.slice(),
        strokeColor: props.color,
        strokeWidth: props.width,
        opacity: props.opacity ?? 1,
        lineCap: 'round',
        lineJoin: 'round',
        tension: 0.5,
        mode: props.mode
      }
    };
  }

  execute(): void {
    // Check shape limits
    const store = useDiagramStore.getState();
    if (!validateShapeLimitOrPromptUpgrade(store)) {
      return; 
    }
    
    const mutationManager = getNodeMutationManager();
    const baseAttrs = this.nodeTemplate.attrs || {};
    const points = Array.isArray(baseAttrs.points) ? (baseAttrs.points as number[]) : [];
    const node: Node = {
      ...this.nodeTemplate,
      attrs: {
        ...baseAttrs,
        points: points.slice()
      }
    };
    mutationManager.addNode(node);
    
    // Track freehand usage (first-time + aggregated)
    SessionMetrics.firstTime('freehand');
    SessionMetrics.increment('freehand_paths_created');
  }

  undo(): void {
    const mutationManager = getNodeMutationManager();
    mutationManager.deleteNode(this.nodeTemplate.id);
  }
}
